import clsx from 'clsx';
import React, { useRef } from 'react';

import TiptapEditor, { TiptapEditorRef } from '../TipTapEditor';

interface TextAreaProps {
  label?: string;
  error?: string;
  className?: string;
  id?: string;
  guideLines?: React.ReactNode;
  required?: boolean;
  checkboxLabel?: string;
  showCheckbox?: boolean;
  secondaryLabel?: string;
  primaryText?: string;
  blockersText?: string;
  isChecked?: boolean;
  primarySuggestion?: React.ReactNode;
  onPrimaryTextChange: (text: string) => void;
  onBlockersTextChange?: (text: string) => void;
  onCheckedChange?: (checked: boolean) => void;
  primaryPlaceholder?: string;
  blockersPlaceholder?: string;
  disabled?: boolean;
}

export function TextArea({
  label,
  error,
  className,
  id,
  guideLines,
  primarySuggestion,
  required,
  checkboxLabel = 'No Blockers',
  showCheckbox,
  secondaryLabel = 'Mention your blockers here',
  primaryText,
  blockersText,
  isChecked,
  onPrimaryTextChange,
  onBlockersTextChange,
  onCheckedChange,
  primaryPlaceholder = 'Write here',
  blockersPlaceholder = 'Write here',
  disabled = false,
}: TextAreaProps): JSX.Element {
  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    onCheckedChange?.(e.target.checked);
  };

  const editorRefs = useRef<Record<string, TiptapEditorRef | null>>({});
  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col items-start gap-[8px] self-stretch w-full">
        {label && (
          <label
            htmlFor={id}
            className="flex-1 text-gray-600 text-md font-normal leading-6"
          >
            {label}
            {required && <span className="text-primary">*</span>}
          </label>
        )}
        {guideLines && guideLines}
        <TiptapEditor
          ref={(el) => {
            editorRefs.current.primary = el;
          }}
          initialContent={primaryText || ''}
          onChange={onPrimaryTextChange}
          placeholder={primaryPlaceholder}
          editable={!disabled}
          minHeight="50px"
          className={clsx(
            'border rounded-lg w-full items-start gap-[8px] self-stretch whitespace-pre-wrap',
            'rounded-[6px] border border-gray-300 ',
            'focus:outline-none',
            'placeholder:text-placeholder',
            error && 'border-red-300 text-red-900 placeholder-red-300',
            disabled
              ? 'bg-[rgba(0,0,0,0.04)] text-[#8C8C8C]'
              : 'bg-[#FEFDFC] text-[#141414]',
            className,
          )}
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        {primarySuggestion && primarySuggestion}
        {showCheckbox && (
          <label htmlFor="checkbox-input" className="flex items-center gap-2 ">
            <input
              id="checkbox-input"
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              disabled={disabled}
              className={clsx(
                'w-4 h-4 rounded-[4px] border border-gray-300 appearance-none relative',
                "before:content-[''] before:absolute before:opacity-0 before:w-[10px] before:h-[6px]",
                'before:border-[2px] before:border-gray-600 before:border-t-0 before:border-r-0',
                'before:rotate-[-45deg] before:top-[45%] before:left-[50%] before:-translate-x-1/2 before:-translate-y-1/2',
                'checked:before:opacity-100',

                disabled
                  ? 'bg-[rgba(0,0,0,0.04)] text-[#8C8C8C]'
                  : 'bg-[#FEFDFC] text-[#141414] checkbox checked:bg-primary checked:border-primary checked:before:border-white',
              )}
            />
            <span className="text-sm text-gray-700">{checkboxLabel}</span>
          </label>
        )}
      </div>

      {/* Secondary TextArea for Blockers */}
      {showCheckbox && !isChecked && (
        <div className="flex flex-col items-start gap-[8px] self-stretch w-full">
          <label
            htmlFor="blockers-input"
            className="flex-1 text-gray-600 text-md font-normal leading-6"
          >
            {secondaryLabel}
            {required && <span className="text-primary">*</span>}
          </label>
          <TiptapEditor
            ref={(el) => {
              editorRefs.current.blockers = el;
            }}
            initialContent={blockersText || ''}
            onChange={onBlockersTextChange}
            placeholder={blockersPlaceholder}
            editable={!disabled}
            minHeight="50px"
            className={clsx(
              'border rounded-lg  items-start gap-[8px] self-stretch w-full ',
              'rounded-[6px] border border-gray-300 ',
              'focus:outline-none',
              'placeholder:text-placeholder',
              error && 'border-red-300 text-red-900 placeholder-red-300',
              disabled
                ? 'bg-[rgba(0,0,0,0.04)] text-[#8C8C8C]'
                : 'bg-[#FEFDFC] text-[#141414]',
              className,
            )}
          />
        </div>
      )}
    </div>
  );
}
